import { useMeta, useProfile, useUpdateProfile } from '@numistic/data'
import { useTranslation } from '@numistic/i18n'
import {
  Alert,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Field,
  Input,
  Select,
} from '@numistic/ui'
import { createRoute } from '@tanstack/react-router'
import { useState } from 'react'

import { PageLoading } from '../../components/page-loading.js'
import { useErrorText } from '../../lib/errors.js'
import { settingsRoute } from './layout.js'

export const profileRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: 'profile',
  component: ProfilePage,
})

function ProfilePage() {
  const profile = useProfile().data
  const meta = useMeta().data
  if (!profile || !meta) return <PageLoading />
  return <ProfileForm key={profile.email} profile={profile} meta={meta} />
}

function ProfileForm({
  profile,
  meta,
}: {
  profile: { name: string; email: string; baseCurrency: string }
  meta: { currencies: Array<{ code: string; name: { ru: string } }> }
}) {
  const { t } = useTranslation()
  const errorText = useErrorText()
  const update = useUpdateProfile()
  const [name, setName] = useState(profile.name)
  const [currency, setCurrency] = useState(profile.baseCurrency)

  return (
    <div className="grid max-w-2xl gap-6">
      <Card>
        <CardHeader>
          <CardTitle>{t('profile.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <form
            className="grid gap-4"
            onSubmit={(e) => {
              e.preventDefault()
              update.mutate({ name: name.trim(), baseCurrency: currency })
            }}
          >
            <Field label={t('profile.email')}>
              <Input value={profile.email} disabled />
            </Field>
            <Field label={t('profile.name')}>
              <Input value={name} onChange={(e) => setName(e.target.value)} required />
            </Field>
            <Field label={t('profile.baseCurrency')} hint={t('profile.baseCurrencyHint')}>
              <Select value={currency} onChange={(e) => setCurrency(e.target.value)}>
                {meta.currencies.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.code} — {c.name.ru}
                  </option>
                ))}
              </Select>
            </Field>
            {update.error ? (
              <Alert variant="destructive">{errorText.request(update.error)}</Alert>
            ) : null}
            {update.isSuccess ? <Alert>{t('profile.saved')}</Alert> : null}
            <Button type="submit" className="justify-self-start" disabled={update.isPending}>
              {t('common.save')}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
